/**
 * CancellationToken
 *
 * Contrato de cancelación cooperativa que atraviesa casos de uso y flows sin
 * arrastrar tipos de plataforma. Deliberadamente NO expone `AbortSignal`:
 * `AbortSignal`/`AbortController` son detalle de infraestructura (la request
 * HTTP de Express), y la capa de aplicación no tiene por qué conocerlos — sólo
 * necesita saber si le pidieron que pare.
 *
 * La infraestructura decide cómo se materializa el token: en un controller es
 * la desconexión del cliente, en un test es un objeto que se marca a mano.
 *
 * Uso desde un caso de uso o flow:
 *
 * ```ts
 * if (input.cancellation?.isCancelled) {
 *   throw new OperationCancelledException('PrescriptionReportExport');
 * }
 * ```
 * @author daas software s.r.l <hola@daas.consulting>
 */
export interface CancellationToken {
  /**
   * `true` en cuanto se pidió la cancelación. Se consulta entre unidades de
   * trabajo (lotes de páginas, pasos costosos) para no emitir trabajo nuevo.
   */
  readonly isCancelled: boolean;

  /**
   * Registra un listener que se dispara al cancelar (o de inmediato si el
   * token ya estaba cancelado). Devuelve la función para desregistrarlo, que
   * hay que llamar siempre al terminar la operación: sin eso, una operación
   * larga acumula listeners sobre el mismo token.
   *
   * Lo usa la infraestructura que necesita reaccionar en el acto en vez de
   * esperar al próximo checkpoint — por ejemplo, para abortar una llamada HTTP
   * ya en vuelo.
   */
  onCancelled(listener: () => void): () => void;
}

/**
 * NoopCancellationToken
 *
 * Token que nunca se cancela. Sirve de default cuando el llamador no aporta
 * uno, para que casos de uso y flows no tengan que ramificar por `undefined`.
 */
export class NoopCancellationToken implements CancellationToken {
  static readonly instance: CancellationToken = new NoopCancellationToken();

  get isCancelled(): boolean {
    return false;
  }

  onCancelled(): () => void {
    return () => {
      /* nada que desregistrar: este token nunca se cancela */
    };
  }
}
